import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import {
  Dialog,
  DialogContent,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { toast } from 'sonner';
import { Image as ImageIcon, Video, X, Sparkles, Clock, Film, Loader2, MapPin, Pencil } from 'lucide-react';
import { ImageEditorModal } from './ImageEditorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REALMS = ['Midgard', 'Asgard', 'Jötunheim', 'Vanaheim', 'Álfheim', 'Niðavellir'];

export function CreatePostModal({ open, onOpenChange, onPostCreated, defaultIsStory = false }) {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [media, setMedia] = useState([]); // [{ url, type, file? }]
  const [editingIndex, setEditingIndex] = useState(null); // index of media item being edited
  const [isStory, setIsStory] = useState(defaultIsStory);
  const [isReel, setIsReel] = useState(false);
  const [hashtags, setHashtags] = useState('');
  const [realm, setRealm] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const uploadAccept = useRef('image/*,video/*');

  // Re-apply defaultIsStory when the modal opens (e.g. triggered from story bar)
  useEffect(() => {
    if (open) setIsStory(defaultIsStory);
  }, [open, defaultIsStory]);

  const resetAll = () => {
    setContent(''); setMedia([]); setEditingIndex(null);
    setIsStory(false); setIsReel(false);
    setHashtags(''); setRealm(''); setProgress(0);
  };

  const openPicker = (accept) => {
    uploadAccept.current = accept;
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (media.length + files.length > 4) {
      toast.error('Maximum 4 media items allowed');
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        const res = await api.post('/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (ev) => {
            if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100));
          },
        });
        const abs = res.data.url?.startsWith('/api/') ? `${BACKEND_URL}${res.data.url}` : res.data.url;
        const type = res.data.media_type === 'video' ? 'video' : 'image';
        // Store original file with images so the editor can start from the original each time
        setMedia((prev) => [...prev, { url: abs, type, ...(type === 'image' ? { file } : {}) }]);
        if (type === 'video') setIsReel((r) => r); // keep user's choice; videos are reel-eligible
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removeMedia = (index) => setMedia((prev) => prev.filter((_, i) => i !== index));

  const computedMediaType = () => {
    if (media.length === 0) return 'none';
    const types = new Set(media.map((m) => m.type));
    if (types.size > 1) return 'mixed';
    return media[0].type;
  };

  const handleSubmit = async () => {
    if (!content.trim() && media.length === 0) {
      toast.error('Please add some content or media');
      return;
    }
    if (isReel && !media.some((m) => m.type === 'video')) {
      toast.error('A Saga needs a video. Add a video or turn off Saga.');
      return;
    }
    setLoading(true);
    try {
      const parsedHashtags = hashtags.split(/[\s,#]+/).filter((t) => t.length > 0).map((t) => t.toLowerCase());
      const contentHashtags = content.match(/#(\w+)/g)?.map((t) => t.slice(1).toLowerCase()) || [];
      const allHashtags = [...new Set([...parsedHashtags, ...contentHashtags])];

      const res = await api.post('/posts', {
        content: content.trim(),
        media_urls: media.map((m) => m.url),
        media_type: computedMediaType(),
        is_story: isStory,
        is_reel: isReel,
        hashtags: allHashtags,
        realm: realm || null,
      });

      toast.success(isStory ? 'Story posted!' : isReel ? 'Saga posted!' : 'Post created!');
      resetAll();
      onOpenChange(false);
      onPostCreated?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (content.trim() || media.length > 0) {
      if (!window.confirm('Discard your post?')) return;
    }
    resetAll();
    onOpenChange(false);
  };

  return (
  <>
    <Dialog open={open} onOpenChange={handleClose}>
      {/*
       * Responsive dialog: flex column with sticky header + scrollable body + sticky footer.
       * max-h uses dvh so it respects the visible viewport even when keyboard is open.
       * overflow-hidden on DialogContent; only the body div scrolls.
       */}
      <DialogContent
        className="sm:max-w-lg bg-card border-border p-0"
        style={{ maxHeight: 'min(90dvh, calc(100dvh - var(--header-h, 3.5rem) - 2rem))' }}
        data-testid="create-post-modal"
      >
        {/*
         * Inner wrapper handles the flex column layout.
         * DialogContent keeps its grid/display untouched to avoid conflicts with Radix.
         * This wrapper fills it and enforces the sticky-header / scroll-body / sticky-footer pattern.
         */}
        <div className="flex flex-col h-full" style={{ maxHeight: 'inherit' }}>

          {/* ── Sticky header ── */}
          <div className="px-5 pt-5 pb-3 shrink-0 border-b border-border">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 shrink-0">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback>{user?.display_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="font-semibold truncate">{user?.display_name}</p>
                <p className="text-sm text-muted-foreground truncate">@{user?.username}</p>
              </div>
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-4">
            <Textarea
              placeholder="What's happening in Midgard?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="min-h-[90px] resize-none bg-muted/50 border-transparent focus:border-primary"
              data-testid="post-content-input"
            />

            {/* Media previews — max-height constrains landscape video/images */}
            {media.length > 0 && (
              <div className={`grid gap-2 ${media.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {media.map((m, index) => (
                  <div
                    key={index}
                    className="relative rounded-xl overflow-hidden bg-black"
                    style={{ maxHeight: '28dvh' }}
                    data-testid={`media-preview-${index}`}
                  >
                    {m.type === 'video' ? (
                      <video src={m.url} className="w-full h-full object-contain" controls playsInline style={{ maxHeight: '28dvh' }} />
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-contain" style={{ maxHeight: '28dvh' }} />
                    )}
                    {/* Edit button — images only, when original file is stored */}
                    {m.type === 'image' && m.file && (
                      <button
                        onClick={() => setEditingIndex(index)}
                        className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium hover:bg-black/80"
                        data-testid={`edit-media-${index}`}
                        aria-label="Edit image"
                      >
                        <Pencil className="w-3 h-3" />
                        Edit
                      </button>
                    )}
                    <button
                      onClick={() => removeMedia(index)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                      data-testid={`remove-media-${index}`}
                      aria-label="Remove media"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="upload-progress">
                <Loader2 className="w-4 h-4 animate-spin text-mg-purple" />
                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-mg-purple transition-all" style={{ width: `${progress}%` }} />
                </div>
                <span>{progress}%</span>
              </div>
            )}

            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFiles} data-testid="media-file-input" />

            {/* Upload buttons */}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => openPicker('image/*')} disabled={uploading || media.length >= 4} data-testid="upload-image-btn">
                <ImageIcon className="w-4 h-4 mr-2 text-emerald-400" /> Photo
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => openPicker('video/*')} disabled={uploading || media.length >= 4} data-testid="upload-video-btn">
                <Video className="w-4 h-4 mr-2 text-sky-400" /> Video
              </Button>
            </div>

            {/* Realm */}
            <div>
              <Label className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Realm (optional)</Label>
              <select
                value={realm}
                onChange={(e) => setRealm(e.target.value)}
                className="mt-1 w-full h-10 rounded-md bg-muted/50 border border-transparent px-3 text-sm focus:border-primary focus:outline-none"
                data-testid="realm-select"
              >
                <option value="">No realm</option>
                {REALMS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Hashtags */}
            <div>
              <Label className="text-sm text-muted-foreground">Hashtags (optional)</Label>
              <Input
                placeholder="valhalla, runes, bifrost"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                className="mt-1 bg-muted/50 border-transparent"
                data-testid="hashtags-input"
              />
            </div>

            {/* Post type switches */}
            <div className="flex flex-wrap gap-4 pt-1 pb-1">
              <div className="flex items-center gap-2">
                <Switch id="story" checked={isStory} onCheckedChange={(c) => { setIsStory(c); if (c) setIsReel(false); }} data-testid="story-switch" />
                <Label htmlFor="story" className="flex items-center gap-1 cursor-pointer"><Clock className="w-4 h-4" /> Story (24h)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="reel" checked={isReel} onCheckedChange={(c) => { setIsReel(c); if (c) setIsStory(false); }} data-testid="reel-switch" />
                <Label htmlFor="reel" className="flex items-center gap-1 cursor-pointer"><Film className="w-4 h-4" /> Saga</Label>
              </div>
            </div>
          </div>

          {/* ── Sticky footer — Cancel + Publish always reachable ── */}
          <div
            className="shrink-0 flex justify-end gap-2 px-5 py-4 border-t border-border bg-card"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
          >
            <Button variant="ghost" onClick={handleClose} className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || uploading || (!content.trim() && media.length === 0)}
              className="rounded-full bg-primary"
              data-testid="submit-post-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Posting...
                </span>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />{isStory ? 'Share Story' : isReel ? 'Post Saga' : 'Post'}</>
              )}
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>

    {/* Image editor — rendered as a separate Dialog above the composer */}
    {editingIndex !== null && media[editingIndex]?.file && (
      <ImageEditorModal
        open={editingIndex !== null}
        onClose={() => setEditingIndex(null)}
        onSave={(newUrl) => {
          setMedia(prev => prev.map((m, i) =>
            i === editingIndex ? { ...m, url: newUrl } : m
          ));
          setEditingIndex(null);
        }}
        file={media[editingIndex].file}
        imageUrl={media[editingIndex].url}
      />
    )}
  </>
  );
}

export default CreatePostModal;
